import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import MercadoPagoCheckoutConfig from 'types/models/MercadoPagoCheckoutConfig';
import EditMPCheckoutConfigContext from 'contexts/EditMPCheckoutConfigContext';
import { getMPCheckoutConfigs } from 'services/mercadoPagoServices';

export default (): JSX.Element => {
  const history = useHistory();
  const [paginationMercadoPagoCheckoutConfig, setPaginationMercadoPagoCheckoutConfigs] =
    useState<PaginationData<MercadoPagoCheckoutConfig> | null>(null);
  const { setSelectedMPCheckoutConfig } = React.useContext(EditMPCheckoutConfigContext);
  const { t } = useTranslation();

  const goToPage = (url: string): void => {
    history.push(url);
  };

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = React.useCallback(async (page?: number) => {
    const paginationData = await getMPCheckoutConfigs(page);
    if (paginationData) setPaginationMercadoPagoCheckoutConfigs(paginationData);
  }, []);

  const renderEditButton = (mercadoPagoCheckoutConfig: MercadoPagoCheckoutConfig): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedMPCheckoutConfig(mercadoPagoCheckoutConfig);
          history.push('mercado-pago-checkout/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: MercadoPagoCheckoutConfig[]): JSX.Element => {
    return (
      <>
        {paginationData.map((mercadoPagoCheckoutConfig: MercadoPagoCheckoutConfig) => (
          <TableRow key={mercadoPagoCheckoutConfig.description}>
            <TableCell component="th" scope="row">
              {mercadoPagoCheckoutConfig.description}
            </TableCell>
            <TableCell>{mercadoPagoCheckoutConfig.mpApiUrl}</TableCell>
            <TableCell className="monospace-value-cell">{mercadoPagoCheckoutConfig.token}</TableCell>
            <TableCell>{renderEditButton(mercadoPagoCheckoutConfig)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div>
      <div className="vertical-bottom-space">
        <Button
          className="btn-50"
          variant="contained"
          color="primary"
          onClick={() => goToPage('mercado-pago-checkout/create')}
        >
          {t('listMPConfigs-page-create')}
        </Button>
      </div>
      <div className="vertical-bottom-space">
        <Button className="btn-50" variant="contained" color="secondary" onClick={() => history.go(-1)}>
          {t('go-back')}
        </Button>
      </div>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listMPConfigs-page-description')}</TableCell>
              <TableCell>{t('listMPCheckoutConfigs-page-api-url')}</TableCell>
              <TableCell>{t('listMPConfigs-page-token')}</TableCell>
              <TableCell>{t('listMPConfigs-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationMercadoPagoCheckoutConfig?.data && paginationMercadoPagoCheckoutConfig?.data.length > 0 ? (
              renderContent(paginationMercadoPagoCheckoutConfig.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listMPConfigs-page-noConfigs')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationMercadoPagoCheckoutConfig && (
        <TablePagination
          component="div"
          count={paginationMercadoPagoCheckoutConfig.total}
          page={paginationMercadoPagoCheckoutConfig.currentPage - 1}
          onPageChange={(_, page: number) => {
            loadData(page + 1);
          }}
          rowsPerPage={paginationMercadoPagoCheckoutConfig.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </div>
  );
};
